/**
 * Creator marketplace (docs/creators/SPEC.md): the shapes the partner API
 * returns for a creator, the lists their forms pick from, and the small
 * helpers every creator screen shares.
 *
 * Every field the API sends is treated as possibly missing. The backend is
 * new, and a media kit that crashes on a null headline is a creator who never
 * comes back to finish it.
 */
import { api } from './api';
import type { Lang } from './i18n';
import type { Tone } from '../components/Primitives';

// ------------------------------------------------------------------- shapes

export type CreatorType = 'influencer' | 'ugc' | 'both';

export interface BrandWorked {
  name: string;
  note?: string | null;
}

export interface ShowcaseItem {
  url: string;
  caption?: string | null;
  platform?: string | null;
  imageUrl?: string | null;
}

export interface CreatorProfile {
  handle: string;
  displayName: string;
  headline?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
  coverUrl?: string | null;
  city?: string | null;
  state?: string | null;
  languages?: string[] | null;
  niches?: string[] | null;
  creatorType?: CreatorType | null;
  audienceNote?: string | null;
  brandsWorked?: BrandWorked[] | null;
  showcase?: ShowcaseItem[] | null;
  acceptsBarter?: boolean | null;
  minBudgetMicros?: string | null;
  isPublic?: boolean | null;
  ratingAvg?: number | null;
  ratingCount?: number | null;
  dealsCompleted?: number | null;
}

export interface CreatorChannel {
  id: string;
  platform: string;
  username: string;
  url?: string | null;
  followers: number;
  avgViews?: number | null;
  engagementPct?: number | null;
  verification?: 'self' | 'api' | null;
  verifyCode?: string | null;
  verifiedAt?: string | null;
  verified?: boolean;
}

export interface CreatorPackage {
  id: string;
  platform: string;
  kind: string;
  title: string;
  description?: string | null;
  priceMicros: string;
  deliveryDays: number;
  revisions: number;
  usageRightsDays?: number | null;
  includesPosting: boolean;
  isActive: boolean;
}

export interface CreatorStats {
  earnedMicros?: string | null;
  pendingMicros?: string | null;
  openDeals?: number | null;
  rating?: number | null;
  ratingCount?: number | null;
  dealsCompleted?: number | null;
}

export interface CreatorHomeResponse {
  profile: CreatorProfile | null;
  channels?: CreatorChannel[] | null;
  packages?: CreatorPackage[] | null;
  stats?: CreatorStats | null;
  publicUrl?: string | null;
}

export interface DealSummary {
  id: string;
  title: string;
  status: string;
  kind: 'package' | 'custom' | string;
  grossMicros?: string | null;
  gstMicros?: string | null;
  dueOn?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  counterpart?: { name?: string | null; photoUrl?: string | null } | null;
  unread?: number | null;
}

export interface Deal extends DealSummary {
  brief?: string | null;
  deliverables?: string[] | null;
  productNote?: string | null;
  referenceLinks?: string[] | null;
  budgetMicros?: string | null;
  commissionMicros?: string | null;
  netMicros?: string | null;
  package?: CreatorPackage | null;
  liveUrls?: string[] | null;
  results?: unknown;
  nextAction?: string | null;
}

export interface DealMessage {
  id: string;
  author: 'brand' | 'creator' | 'system';
  kind: 'message' | 'draft' | 'live' | 'status' | string;
  body?: string | null;
  files?: string[] | null;
  createdAt: string;
}

export interface VerifyResponse {
  verified: boolean;
  verifyCode?: string | null;
  instructions?: string | null;
  followers?: number | null;
  message?: string | null;
}

export interface RateSuggestion {
  lowMicros: string;
  midMicros: string;
  highMicros: string;
  note?: string | null;
}

// --------------------------------------------------------------------- lists

type Label = { hi: string; en: string };
export const pick = (l: Label | undefined, lang: Lang, fallback: string) =>
  l ? l[lang] : fallback;

export const PLATFORMS: Record<string, Label & { colour: string; base?: string }> = {
  instagram: {
    hi: 'Instagram',
    en: 'Instagram',
    colour: '#d6249f',
    base: 'https://instagram.com/',
  },
  youtube: { hi: 'YouTube', en: 'YouTube', colour: '#e11d1d', base: 'https://youtube.com/@' },
  facebook: {
    hi: 'Facebook',
    en: 'Facebook',
    colour: '#1877f2',
    base: 'https://facebook.com/',
  },
  threads: { hi: 'Threads', en: 'Threads', colour: '#111827', base: 'https://threads.net/@' },
  x: { hi: 'X (Twitter)', en: 'X (Twitter)', colour: '#0f172a', base: 'https://x.com/' },
  linkedin: {
    hi: 'LinkedIn',
    en: 'LinkedIn',
    colour: '#0a66c2',
    base: 'https://linkedin.com/in/',
  },
  sharechat: { hi: 'ShareChat', en: 'ShareChat', colour: '#f59e0b' },
  moj: { hi: 'Moj', en: 'Moj', colour: '#f97316' },
  josh: { hi: 'Josh', en: 'Josh', colour: '#7c3aed' },
  other: { hi: 'अन्य', en: 'Other', colour: '#64748b' },
};
export const PLATFORM_KEYS = Object.keys(PLATFORMS);
export const platformLabel = (p: string, lang: Lang) => pick(PLATFORMS[p], lang, p);

export const KINDS: Record<string, Label> = {
  reel: { hi: 'रील', en: 'Reel' },
  post: { hi: 'पोस्ट', en: 'Post' },
  story: { hi: 'स्टोरी', en: 'Story' },
  carousel: { hi: 'कैरोसेल', en: 'Carousel' },
  short: { hi: 'शॉर्ट', en: 'Short' },
  video: { hi: 'वीडियो', en: 'Video' },
  integration: { hi: 'वीडियो में ब्रांड', en: 'Integration' },
  live: { hi: 'लाइव', en: 'Live' },
  ugc_video: { hi: 'UGC वीडियो', en: 'UGC video' },
  ugc_photo: { hi: 'UGC फ़ोटो', en: 'UGC photo' },
  event: { hi: 'इवेंट में आना', en: 'Event appearance' },
  store_visit: { hi: 'दुकान विज़िट', en: 'Store visit' },
  review: { hi: 'रिव्यू', en: 'Review' },
  other: { hi: 'अन्य', en: 'Other' },
};
export const KIND_KEYS = Object.keys(KINDS);
export const kindLabel = (k: string, lang: Lang) => pick(KINDS[k], lang, k.replace(/_/g, ' '));

/** Niches Indian brands actually search by. Custom ones are allowed too. */
export const NICHES: Array<[string, string, string]> = [
  ['fashion', 'फ़ैशन', 'Fashion'],
  ['beauty', 'ब्यूटी / मेकअप', 'Beauty & makeup'],
  ['skincare', 'स्किनकेयर', 'Skincare'],
  ['food', 'खाना / फ़ूड', 'Food'],
  ['travel', 'घूमना / ट्रैवल', 'Travel'],
  ['tech', 'टेक / गैजेट', 'Tech & gadgets'],
  ['finance', 'पैसा / फ़ाइनेंस', 'Personal finance'],
  ['fitness', 'फ़िटनेस', 'Fitness'],
  ['health', 'सेहत', 'Health & wellness'],
  ['parenting', 'पेरेंटिंग', 'Parenting'],
  ['comedy', 'कॉमेडी', 'Comedy'],
  ['education', 'पढ़ाई / एजुकेशन', 'Education'],
  ['career', 'करियर / जॉब', 'Careers & jobs'],
  ['gaming', 'गेमिंग', 'Gaming'],
  ['lifestyle', 'लाइफ़स्टाइल', 'Lifestyle'],
  ['devotional', 'भक्ति / धार्मिक', 'Devotional'],
  ['regional_cinema', 'क्षेत्रीय सिनेमा', 'Regional cinema'],
  ['music', 'संगीत', 'Music'],
  ['dance', 'डांस', 'Dance'],
  ['auto', 'गाड़ी / ऑटो', 'Auto'],
  ['real_estate', 'प्रॉपर्टी', 'Real estate'],
  ['home_decor', 'घर सजावट', 'Home decor'],
  ['cooking', 'रसोई / रेसिपी', 'Recipes & cooking'],
  ['agriculture', 'खेती / किसान', 'Agriculture'],
  ['sports', 'खेल / क्रिकेट', 'Sports & cricket'],
  ['pets', 'पालतू जानवर', 'Pets'],
  ['wedding', 'शादी', 'Weddings'],
  ['motivation', 'मोटिवेशन', 'Motivation'],
  ['news', 'न्यूज़ / करंट अफ़ेयर्स', 'News & current affairs'],
  ['local', 'लोकल शहर', 'Local / city'],
  ['vlogs', 'व्लॉग', 'Daily vlogs'],
  ['books', 'किताबें', 'Books'],
  ['photography', 'फ़ोटोग्राफ़ी', 'Photography'],
  ['art', 'आर्ट / DIY', 'Art & DIY'],
  ['startups', 'बिज़नेस / स्टार्टअप', 'Business & startups'],
];
export const nicheLabel = (n: string, lang: Lang) => {
  const row = NICHES.find(([k]) => k === n);
  return row ? (lang === 'hi' ? row[1] : row[2]) : n;
};

export const LANGUAGES: Array<[string, string, string]> = [
  ['hindi', 'हिन्दी', 'Hindi'],
  ['english', 'अंग्रेज़ी', 'English'],
  ['hinglish', 'हिंग्लिश', 'Hinglish'],
  ['bhojpuri', 'भोजपुरी', 'Bhojpuri'],
  ['haryanvi', 'हरियाणवी', 'Haryanvi'],
  ['punjabi', 'पंजाबी', 'Punjabi'],
  ['rajasthani', 'राजस्थानी', 'Rajasthani'],
  ['marathi', 'मराठी', 'Marathi'],
  ['gujarati', 'गुजराती', 'Gujarati'],
  ['bengali', 'बांग्ला', 'Bengali'],
  ['tamil', 'तमिल', 'Tamil'],
  ['telugu', 'तेलुगु', 'Telugu'],
  ['kannada', 'कन्नड़', 'Kannada'],
  ['malayalam', 'मलयालम', 'Malayalam'],
  ['odia', 'ओड़िया', 'Odia'],
  ['assamese', 'असमिया', 'Assamese'],
  ['urdu', 'उर्दू', 'Urdu'],
];
export const languageLabel = (n: string, lang: Lang) => {
  const row = LANGUAGES.find(([k]) => k === n);
  return row ? (lang === 'hi' ? row[1] : row[2]) : n;
};

export const STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Chandigarh',
  'Andaman and Nicobar Islands',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep',
];

// --------------------------------------------------------------- deal status

/** Inbox tabs, in the order a creator works through them. */
export type DealBucket = 'new' | 'active' | 'done' | 'closed';
export const BUCKET_OF: Record<string, DealBucket> = {
  requested: 'new',
  accepted: 'active',
  in_progress: 'active',
  draft_submitted: 'active',
  changes_requested: 'active',
  approved: 'active',
  live: 'active',
  disputed: 'active',
  completed: 'done',
  declined: 'closed',
  cancelled: 'closed',
};
export const bucketOf = (status: string): DealBucket => BUCKET_OF[status] ?? 'active';

export const DEAL_STATUS: Record<string, Label & { tone: Tone; needsYou?: boolean }> = {
  requested: { hi: 'नई रिक्वेस्ट', en: 'New request', tone: 'info', needsYou: true },
  accepted: { hi: 'ब्रांड के पेमेंट का इंतज़ार', en: 'Waiting for payment', tone: 'neutral' },
  in_progress: {
    hi: 'पेमेंट हो गया — काम शुरू',
    en: 'Paid — start work',
    tone: 'warn',
    needsYou: true,
  },
  draft_submitted: { hi: 'ड्राफ़्ट भेजा', en: 'Draft sent', tone: 'neutral' },
  changes_requested: { hi: 'बदलाव चाहिए', en: 'Changes asked', tone: 'warn', needsYou: true },
  approved: {
    hi: 'मंज़ूर — पोस्ट करें',
    en: 'Approved — go live',
    tone: 'good',
    needsYou: true,
  },
  live: { hi: 'लाइव', en: 'Live', tone: 'good' },
  completed: { hi: 'पूरा', en: 'Completed', tone: 'good' },
  declined: { hi: 'मना किया', en: 'Declined', tone: 'neutral' },
  cancelled: { hi: 'रद्द', en: 'Cancelled', tone: 'neutral' },
  disputed: { hi: 'शिकायत', en: 'Disputed', tone: 'bad' },
};
export const dealStatusLabel = (s: string, lang: Lang) =>
  pick(DEAL_STATUS[s], lang, s.replace(/_/g, ' '));
export const needsYou = (s: string) => Boolean(DEAL_STATUS[s]?.needsYou);

// ------------------------------------------------------------------- helpers

export const str = (v: string | null | undefined) => (v ?? '').trim();
export const arr = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

/** Micro-rupee string → plain rupees for an input box ("8000", "8000.5"). */
export function microsToRupees(micros: string | null | undefined): string {
  if (!micros) return '';
  try {
    const v = BigInt(micros);
    const whole = v / 1_000_000n;
    const paise = (v % 1_000_000n) / 10_000n;
    return paise === 0n ? String(whole) : `${whole}.${String(paise).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

/** A rupee amount typed by a person, cleaned to what the API parses. */
export function cleanRupees(s: string): string {
  const v = s.replace(/[₹,\s]/g, '');
  return /^\d+(\.\d{1,2})?$/.test(v) ? v : '';
}

/** 12,400 → "12.4K"; 1,250,000 → "12.5L" — how Indian creators say it. */
export function compactCount(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0';
  if (v >= 10_000_000) return `${trim1(v / 10_000_000)}Cr`;
  if (v >= 100_000) return `${trim1(v / 100_000)}L`;
  if (v >= 1_000) return `${trim1(v / 1_000)}K`;
  return String(Math.round(v));
}
const trim1 = (x: number) => (Math.round(x * 10) / 10).toString();

export const isVerified = (c: CreatorChannel) => Boolean(c.verified ?? c.verifiedAt);

/** Upload a photo or file the way proof does; returns the stored file's URL. */
export async function uploadFile(dataUrl: string): Promise<string> {
  const r = await api.post<{ url?: string; fileUrl?: string }>('/v1/partner/uploads', {
    data: dataUrl,
  });
  const url = r.url ?? r.fileUrl;
  if (!url) throw new Error('upload failed');
  return url;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older Android WebViews: the textarea trick still works there.
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      el.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export const whatsappShare = (text: string) =>
  `https://wa.me/?text=${encodeURIComponent(text)}`;

/** Links typed one per line (or space/comma separated) → clean URLs. */
export function parseLinks(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (/^https?:\/\//i.test(s) ? s : `https://${s}`));
}

export const isImageUrl = (u: string) => /\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(u);

/** A micro-rupee string as a BigInt, 0 for anything unparsable. */
export function big(s: string | null | undefined): bigint {
  try {
    return BigInt(s || '0');
  } catch {
    return 0n;
  }
}

/** The cheapest active package's price: "from ₹X" on the card. */
export function lowestPrice(packages: CreatorPackage[]): string | null {
  let best: string | null = null;
  for (const p of packages) {
    if (p.isActive === false) continue;
    if (best === null || big(p.priceMicros) < big(best)) best = p.priceMicros;
  }
  return best;
}
